import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, ref } from "vue";

import LoadingSpinner from "../../components/loaders/LoadingSpinner.vue";
import ButtonCommon from "../buttons/ButtonCommon.vue";

import ThemeService from "../../service/themes.js";

/**
 * Types
 */

type Props = {
  alt?: string;
  aspectRatio?: string | number;
  contentClass?: string;
  errorText: string;
  failedToLoadText: string;
  fallbackSrc?: string | object;
  gradient?: string;
  height?: number | string;
  isDark?: boolean;
  isEager?: boolean;
  lazySrc?: string;
  light?: boolean;
  maxHeight?: number | string;
  maxWidth?: number | string;
  minHeight?: number | string;
  minWidth?: number | string;
  options?: object;
  position?: string;
  progressCircleSize?: number | string;
  retryButtonOffsetY?: number | string;
  retryButtonText: string;
  shouldContain?: boolean;
  sizes?: string;
  src: string | object;
  srcset?: string;
  width?: number | string;
  shouldShowRetryButton?: boolean; // If no retry button => auto retry
  errorIconSize?: number | string;
};

/**
 * Utils
 */


export default /*#__PURE__*/_defineComponent({
  __name: 'ImageViewer',
  props: {
    alt: { default: undefined },
    aspectRatio: { default: undefined },
    contentClass: { default: undefined },
    errorText: null,
    failedToLoadText: null,
    fallbackSrc: { default: undefined },
    gradient: { default: undefined },
    height: { default: undefined },
    isDark: { type: Boolean },
    isEager: { type: Boolean, default: false },
    lazySrc: { default: undefined },
    light: { type: Boolean },
    maxHeight: { default: undefined },
    maxWidth: { default: undefined },
    minHeight: { default: undefined },
    minWidth: { default: undefined },
    options: { default: () => ({}) },
    position: { default: "center center" },
    progressCircleSize: { default: 20 },
    retryButtonOffsetY: { default: -18 },
    retryButtonText: null,
    shouldContain: { type: Boolean, default: false },
    sizes: { default: undefined },
    src: { default: undefined },
    srcset: { default: undefined },
    width: { default: undefined },
    shouldShowRetryButton: { type: Boolean, default: true },
    errorIconSize: { default: '48' }
  },
  setup(__props: any) {

const props = __props as { alt: string, aspectRatio: string | number, contentClass: string, errorText: string, failedToLoadText: string, fallbackSrc: string | object, gradient: string, height: number | string, isDark?: boolean, isEager: boolean, lazySrc: string, light?: boolean, maxHeight: number | string, maxWidth: number | string, minHeight: number | string, minWidth: number | string, options: object, position: string, progressCircleSize: number | string, retryButtonOffsetY: number | string, retryButtonText: string, shouldContain: boolean, sizes: string, src: string | object, srcset: string, width: number | string, shouldShowRetryButton: boolean, errorIconSize: number | string };

const getImageBackgroundColors = () => {
  const BLACK_COLOR_CODE = "#000";

  return {
    black: BLACK_COLOR_CODE,
    primary: new ThemeService().defaultColors.primary,
  };
};

/**
 * Constants
 */

const MAXIMUM_RETRY_COUNT = 2;

/**
 * Component
 */



const imageRetryCount = ref<number>(0);

const imageBackgroundColor = ref<string>(getImageBackgroundColors().primary);

const error = ref<Error | null>(null);

const isLoading = ref<boolean>(false);

const componentKey = computed(
  () => `${Math.random()}-${imageRetryCount.value}-key`
);

const imageStyle = computed(() => ({
  backgroundColor: imageBackgroundColor.value,
}));

const shouldShowErrorState = computed(
  () => imageRetryCount.value >= MAXIMUM_RETRY_COUNT && !isLoading.value
);

const shouldShowRetryState = computed(
  () =>
    props.shouldShowRetryButton && error.value && !shouldShowErrorState.value
);

const currentImgSrc = computed(() => {
  switch (imageRetryCount.value) {
    case 0:
      return props.src;

    case 1:
    case 2:
      return props.fallbackSrc || props.src; // Switch to src if no fallbackSrc

    default:
      return "";
  }
});

const errorContainerStyle = computed(() => ({
  transform: `translateY(${props.retryButtonOffsetY}px)`,
}));

const handleImageLoad = () => {
  imageBackgroundColor.value = props.src
    ? getImageBackgroundColors().black
    : getImageBackgroundColors().primary;

  isLoading.value = false;
  error.value = null;
};

const handleImageError = async () => {
  if (!props.shouldShowRetryButton) {
    // If no retry button => call its click handler
    handleRetryClick();

    await nextTick();

    // To skip error states during retry
    if (imageRetryCount.value === MAXIMUM_RETRY_COUNT) {
      return;
    }
  }

  imageBackgroundColor.value = getImageBackgroundColors().primary;

  isLoading.value = false;
  error.value = new Error("Something went wrong!");
};

const handleRetryClick = () => {
  imageRetryCount.value += 1;
  isLoading.value = true;
};

const handleLoadstart = () => {
  error.value = null;
  isLoading.value = true;
};

return { __sfc: true,getImageBackgroundColors, MAXIMUM_RETRY_COUNT, props, imageRetryCount, imageBackgroundColor, error, isLoading, componentKey, imageStyle, shouldShowErrorState, shouldShowRetryState, currentImgSrc, errorContainerStyle, handleImageLoad, handleImageError, handleRetryClick, handleLoadstart, LoadingSpinner, ButtonCommon }
}

})